import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus, KeyRound, Pencil, Trash2, X } from "lucide-react";
import { usersApi, authApi } from "../api";
import type { User } from "../types";

const createSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(8).max(256),
  role: z.enum(["admin", "editor"]),
  display_name: z.string().max(128).optional(),
  email: z.string().email().max(256).optional().or(z.literal("")),
});
type CreateValues = z.infer<typeof createSchema>;

export default function UsersPage() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: authApi.me });
  const users = useQuery({ queryKey: ["admin-users"], queryFn: usersApi.list });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);

  const update = useMutation({
    mutationFn: ({ username, patch }: { username: string; patch: Partial<User> }) =>
      usersApi.update(username, patch),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error((e as { message?: string }).message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: (username: string) => usersApi.remove(username),
    onSuccess: () => {
      toast.success("User deleted");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  return (
    <div className="max-w-5xl mx-auto px-10 py-10">
      <header className="mb-7 flex items-end justify-between">
        <div>
          <h1 className="display text-[36px] font-medium tracking-tight">
            <span className="italic">Users</span>
          </h1>
          <p className="text-[13.5px] text-ink-500 mt-1.5">
            Manage who can sign in to the admin.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">
          <Plus className="size-3.5" /> New user
        </button>
      </header>

      <div className="surface overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-cream-50 eyebrow !tracking-[0.1em]">
            <tr>
              <th className="text-left px-3.5 py-2.5 font-medium">Username</th>
              <th className="text-left px-3.5 py-2.5 font-medium">Name</th>
              <th className="text-left px-3.5 py-2.5 font-medium">Role</th>
              <th className="text-left px-3.5 py-2.5 font-medium">Last login</th>
              <th className="text-left px-3.5 py-2.5 font-medium">Status</th>
              <th className="text-right px-3.5 py-2.5 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {users.data?.users.map((u) => (
              <tr key={u.username} className="hover:bg-cream-50 group">
                <td className="px-3.5 py-2.5 font-mono text-[12.5px]">{u.username}</td>
                <td className="px-3.5 py-2.5">{u.display_name ?? "—"}</td>
                <td className="px-3.5 py-2.5 capitalize text-ink-700">{u.role}</td>
                <td className="px-3.5 py-2.5 text-ink-400 text-[12px]">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}
                </td>
                <td className="px-3.5 py-2.5">
                  {u.disabled ? (
                    <span className="chip !bg-accent-50 !text-accent-700">Disabled</span>
                  ) : (
                    <span className="chip !bg-emerald-50 !text-emerald-700">Active</span>
                  )}
                </td>
                <td className="px-3.5 py-2.5 text-right">
                  <div className="inline-flex gap-0.5">
                    <button onClick={() => setResetting(u)} className="btn-icon" title="Reset password">
                      <KeyRound className="size-3.5" />
                    </button>
                    <button onClick={() => setEditing(u)} className="btn-icon" title="Edit">
                      <Pencil className="size-3.5" />
                    </button>
                    <button
                      disabled={u.username === me.data?.user?.username}
                      onClick={() => {
                        if (confirm(`Delete user "${u.username}"?`)) remove.mutate(u.username);
                      }}
                      className="btn-icon hover:!bg-accent-50 hover:!text-accent-600 disabled:opacity-40 disabled:hover:!bg-transparent disabled:hover:!text-ink-500"
                      title={u.username === me.data?.user?.username ? "Cannot delete yourself" : "Delete"}
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin-users"] })}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSave={(patch) => update.mutate({ username: editing.username, patch })}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["admin-users"] })}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { role: "editor" },
  });

  async function submit(v: CreateValues) {
    try {
      await usersApi.create({
        username: v.username,
        password: v.password,
        role: v.role,
        display_name: v.display_name || undefined,
        email: v.email || undefined,
      });
      toast.success("User created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Create failed");
    }
  }

  return (
    <Modal title="New user" onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Username</label>
          <input className="input font-mono" {...register("username")} />
          {errors.username && <p className="text-[11.5px] text-accent-600 mt-1">{errors.username.message}</p>}
        </div>
        <div>
          <label className="label">Password</label>
          <input type="password" className="input" {...register("password")} />
          {errors.password && <p className="text-[11.5px] text-accent-600 mt-1">{errors.password.message}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Display name</label>
            <input className="input" {...register("display_name")} />
          </div>
          <div>
            <label className="label">Role</label>
            <select className="input" {...register("role")}>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" {...register("email")} />
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 mt-4">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSave,
}: {
  user: User;
  onClose: () => void;
  onSave: (patch: Partial<User>) => void;
}) {
  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [email, setEmail] = useState(user.email ?? "");
  const [role, setRole] = useState<"admin" | "editor">(user.role);
  const [disabled, setDisabled] = useState(!!user.disabled);

  return (
    <Modal title={`Edit ${user.username}`} onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Display name</label>
          <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">Role</label>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as "admin" | "editor")}>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" checked={disabled} onChange={(e) => setDisabled(e.target.checked)} /> Disabled
        </label>
        <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 mt-4">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => {
              onSave({ display_name: displayName, email, role, disabled });
              onClose();
            }}
            className="btn-primary"
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await usersApi.resetPassword(user.username, pw);
      toast.success(`Password reset for ${user.username}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Reset failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Reset password — ${user.username}`} onClose={onClose}>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">New password</label>
          <input type="password" className="input" value={pw} onChange={(e) => setPw(e.target.value)} />
          <p className="text-[11.5px] text-ink-500 mt-1">User will be forced to sign in again.</p>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 mt-4">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary">
            {busy ? "Resetting…" : "Reset password"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4 animate-fade-in">
      <div className="card !rounded-2xl w-full max-w-md shadow-modal animate-scale-in">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
          <h2 className="display text-[18px] font-medium tracking-tight">{title}</h2>
          <button onClick={onClose} className="btn-icon" aria-label="close">
            <X className="size-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
